import { useEffect } from 'react';
import type { LovemoConfig } from '@/hooks/useLovemoConfig';

interface LovemoDownloadModalProps {
  open: boolean;
  onClose: () => void;
  config: LovemoConfig;
}

export default function LovemoDownloadModal({ open, onClose, config }: LovemoDownloadModalProps) {
  useEffect(() => {
    if (!open) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const platforms = [
    {
      key: 'android',
      name: 'Android',
      hint: '安卓手机 / 平板',
      icon: 'ri-android-line',
      href: config.downloads.android,
      tone: 'bg-primary-500 text-background-50 dark:text-background-950',
    },
    {
      key: 'ios',
      name: 'iOS',
      hint: 'iPhone / iPad',
      icon: 'ri-apple-line',
      href: config.downloads.ios,
      tone: 'bg-accent-500 text-background-50 dark:text-background-950',
    },
    {
      key: 'harmonyos',
      name: 'HarmonyOS',
      hint: '鸿蒙设备',
      icon: 'ri-smartphone-line',
      href: config.downloads.harmonyos,
      tone: 'bg-secondary-500 text-background-50 dark:text-background-950',
    },
    {
      key: 'windows',
      name: 'Windows',
      hint: 'Win 10 / 11 电脑',
      icon: 'ri-windows-line',
      href: config.downloads.windows,
      tone: 'bg-foreground-800 text-background-50 dark:bg-foreground-200 dark:text-foreground-950',
    },
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="关闭下载弹窗"
        onClick={onClose}
        className="absolute inset-0 cursor-pointer bg-foreground-950/60 backdrop-blur-sm"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="选择下载设备"
        className="animate-lovemo-pop relative z-10 w-full max-w-[520px] rounded-2xl border border-background-200/80 bg-background-50 p-6 md:p-8"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="关闭"
          className="absolute right-4 top-4 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900"
        >
          <i className="ri-close-line text-lg" aria-hidden="true" />
        </button>

        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xl">
            <i className="ri-heart-3-fill animate-lovemo-heart" aria-hidden="true" />
          </span>
          <div>
            <h3 className="font-heading text-lg font-bold text-foreground-950">选择你的设备下载</h3>
            <p className="mt-0.5 text-xs text-foreground-600">官方正版，安装包安全无广告</p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {platforms.map((platform) => (
            <a
              key={platform.key}
              href={platform.href}
              className="group flex items-center gap-3 rounded-xl border border-background-200/80 bg-background-100 px-4 py-3.5 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-300 hover:bg-primary-50"
            >
              <span
                className={`flex h-10 w-10 items-center justify-center rounded-lg text-xl ${platform.tone}`}
              >
                <i className={platform.icon} aria-hidden="true" />
              </span>
              <span className="flex flex-col">
                <span className="font-label text-sm font-bold text-foreground-950">{platform.name}</span>
                <span className="text-xs text-foreground-600">{platform.hint}</span>
              </span>
              <i
                className="ri-arrow-down-circle-line ml-auto text-lg text-foreground-300 transition-colors group-hover:text-primary-500"
                aria-hidden="true"
              />
            </a>
          ))}
        </div>

        <p className="mt-6 rounded-xl bg-accent-50 px-4 py-3 text-center text-xs leading-relaxed text-accent-900">
          同步支持Lovemo最新版官网下载，安装遇到问题可查看页面中的Lovemo下载教程。
        </p>
      </div>
    </div>
  );
}